import React, { FC, useState, useRef, useEffect, RefObject } from 'react'
import {
  ConfirmDialog, Centralizer, Container, Group, ChangeDialog, DataTable
  , Button, Grid, TextField, Select, WriteToDeleteDialog,
  Form,
  Accordion,
  Switch
} from '../components';
import { useTranslate, useLogout, useAxiosPrivate, useJSONState, useStaticData } from '../hooks';
import { useNavigate } from 'react-router-dom';
import { AddressType, Prettify } from '../types';
import { countryList, areIdentical } from '../utils';
import { toast } from 'sonner';
import { AddressRegisterType } from '../types/Address';
import DeliveryTypes from './DeliveryTypes';
import { add } from 'date-fns';
import { JsxElement } from 'typescript';

type deliveryType = {
  id: string,
  name: string,
  carriersuiteId: string,
  incotermVersion: {
    dom: string | null,
    eu: string | null,
    ex: string | null,
  }
}

const AddressRegister = () => {
  const { staticData } = useStaticData()
  const api = useAxiosPrivate();
  const [dialog, setDialog] = useState<any>(null);
  const [addresses, setAddresses] = useState<AddressRegisterType[]>([])
  const [deliveryTypes, setDeliveryTypes] = useState<Prettify<deliveryType>[]>([])
  const [originalAddressForm, setOriginalAddressForm] = useState<AddressRegisterType>()
  const [addressForm, setAddressFormProp, setAddressForm] = useJSONState<AddressRegisterType | undefined>();
  const addressFormRef = useRef<HTMLFieldSetElement>(null);
  const [changed, setChanged] = useState(false)
  const { t } = useTranslate();
  const [addressToDelete, setAddressToDelete] = useState("");
  const [ combiterms, setCombiterms ] = useState<{name: string, value:string}[]>([]);

  const onSave = async (e: React.FormEvent<HTMLFormElement>) => {
    // console.log({addressForm})
    // return
    e.preventDefault();
    try {
      const response = await api.put({
        endpoint: `/addressRegister`,
        data: addressForm,
        mount: {
          loadingText: t("saving")
        }
      })
      setAddresses(response.data.addresses ?? [])
      setOriginalAddressForm(addressForm)
    } catch (e) {
      console.error(e)
    }
  }

  const getAddresses = async () => {
    try {
      setAddresses([])
      const response = await api.get({ endpoint: "/addressRegister", mount: null })
      
      setAddresses(response.data?.addresses ?? [])
      setDeliveryTypes(response.data?.deliveryTypes ?? [])
    } catch (e) {
      console.error(e)
    }
  }

  const deleteAddress = async () => {
    try {
      const response = await api.delete({
        endpoint: `/addressRegister/${addressToDelete}`,
        mount: {
          loadingText: t("deleting")
        }
      })
      setAddresses(response.data?.addresses ?? [])
      setAddressForm(undefined)
      setAddressToDelete("")
      toast.success(t("addressDeleted"))
      setChanged(false)
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    getAddresses()
  }, [])

  useEffect(() => {
    setChanged(!areIdentical(addressForm, originalAddressForm))
  }, [addressForm, originalAddressForm])

  useEffect(() => {
    if(!staticData || !addressForm?.countryCode || !addressForm.prefill?.deliveryType) return setCombiterms([]);
    
    const tradeArea = staticData.countries.find(c => c.value === addressForm?.countryCode)?.tradeArea;

    if(!tradeArea) return setCombiterms([]);
    
    const deliveryType = deliveryTypes.find(d => d.id === addressForm?.prefill?.deliveryType);

    if(!deliveryType) return setCombiterms([]);

    const incotermVersion = deliveryType.incotermVersion[tradeArea] ?? "Incoterms 2020";

    setCombiterms(staticData.deliveryTerms[incotermVersion] ?? []);
  }, [staticData, addressForm?.prefill?.deliveryType, addressForm?.countryCode])

  const addressFormElement = () => {
    if (!addressForm) return <></>
    return <Group>
      <Centralizer>
        <Container>
          <Group label={t("address")} ref={addressFormRef}>
            <Grid >
              <TextField
                required
                value={addressForm.addressId}
                onChange={(v) => setAddressFormProp(v.target.value, "addressId")}
              >{t("addressId")}</TextField>
              <TextField
                required
                value={addressForm.name}
                onChange={(v) => setAddressFormProp(v.target.value, "name")}
              >{t("name")}</TextField>
              <TextField
                required
                value={addressForm.street1}
                onChange={(v) => setAddressFormProp(v.target.value, "street1")}
              >{t("street1")}</TextField>
              <TextField
                value={addressForm.street2}
                onChange={(v) => setAddressFormProp(v.target.value, "street2")}
              >{t("street2")}</TextField>
              <TextField
                required
                value={addressForm.zipCode}
                onChange={(v) => setAddressFormProp(v.target.value, "zipCode")}
              >{t("zipCode")}</TextField>
              <TextField
                required
                value={addressForm.city}
                onChange={(v) => setAddressFormProp(v.target.value, "city")}
              >{t("city")}</TextField>
              <Select
                //required={isRequired('countryCode',addressSchema)}
                label={t("country")}
                data={countryList}
                value={addressForm.countryCode ?? "SE"}
                onChange={(e) => { setAddressFormProp(e.target.value, 'countryCode') }}
              // readOnly={locked}
              ></Select>
            </Grid>
          </Group>
          <Group label={t("contact")}>
            <Grid>
              <TextField
                value={addressForm.contact?.name}
                onChange={(v) => setAddressFormProp(v.target.value, "contact", "name")}
              >{t("name")}</TextField>
              <TextField
                value={addressForm.contact?.email}
                onChange={(v) => setAddressFormProp(v.target.value, "contact", "email")}
                _type='email'
              >{t("email")}</TextField>
              <TextField
                value={addressForm.contact?.phoneNo}
                onChange={(v) => setAddressFormProp(v.target.value, "contact", "phoneNo")}
                _type='tel'
              >{t("phone")}</TextField>
            </Grid>
          </Group>
          <Group label={t("information")}>
            <Grid>
              <TextField
                value={addressForm.vatNo}
                onChange={(v) => setAddressFormProp(v.target.value, "vatNo")}
              >{t("vatNo")}</TextField>
            </Grid>
          </Group>
          <Group label={t("prefill")}>
            <Grid>
              <Select
                data={deliveryTypes}
                valueKey='id'
                label={t("deliveryType")}
                value={addressForm.prefill?.deliveryType ?? ""}
                onChange={(v) => setAddressFormProp(v.target.value, "prefill", "deliveryType")}
              />
              <TextField
                value={addressForm.prefill?.customerNumber}
                onChange={(v) => setAddressFormProp(v.target.value, "prefill", "customerNumber")}
              >{t("customerNumber")}</TextField>
              <TextField
                value={addressForm.prefill?.pallRegNo}
                onChange={(v) => setAddressFormProp(v.target.value, "prefill", "pallRegNo")}
              >{t("palletRegno")}</TextField>
              <Select
                data={combiterms}
                label={t("incoterm")}
                value={addressForm.prefill?.combiterms ?? ""}
                onChange={(v) => setAddressFormProp(v.target.value, "prefill", "combiterms")}
              />
              <Switch
                label={t("receiverPays")}
                checked={addressForm.prefill?.receiverPays || false}
                onChange={(v) => setAddressFormProp(v.target.checked, "prefill", "receiverPays")}
              />
            </Grid>
            <Group label={t("goodsLine")}>
              <Grid>
                <Select
                  data={staticData ? staticData.packageTypes : []}
                  label={t("type")}
                  value={addressForm.prefill?.goodsLines?.[0].type ?? ""}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "type")}
                />
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].description}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "description")}
                >{t("description")}</TextField>
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].quantity}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "quantity")}
                  _type='number'
                  decimals={0}
                >{t("quantity")}</TextField>
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].weight}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "weight")}
                  _type='number'
                  adornment='kg'
                  decimals={3}
                >{t("weight")}</TextField>
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].length}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "length")}
                  _type='number'
                  adornment='mm'
                  decimals={0}
                >{t("length")}</TextField>
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].width}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "width")}
                  _type='number'
                  adornment='mm'
                  decimals={0}
                >{t("width")}</TextField>
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].height}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "height")}
                  _type='number'
                  adornment='mm'
                  decimals={0}
                >{t("height")}</TextField>
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].volume}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "volume")}
                  _type='number'
                  decimals={3}
                  adornment='m³'
                >{t("volume")}</TextField>
                <TextField
                  value={addressForm.prefill?.goodsLines?.[0].loadmeter}
                  onChange={(v) => setAddressFormProp(v.target.value, "prefill", "goodsLines", 0, "loadmeter")}
                  _type='number'
                  decimals={2}
                >{t("loadmeters")}</TextField>
              </Grid>
            </Group>
          </Group>
        </Container>
      </Centralizer>
      {
        originalAddressForm?.addressId ?
          <Button
            onClick={() => setAddressToDelete(addressForm.addressId as string)}
            variant='delete'
            _type='button'
          >{t("delete")}</Button>
          : <></>
      }
    </Group>
  }

  if (!staticData) return <></>

  return (<div className='schipt-addressregister'>
    <Form onSubmit={(e) => onSave(e)}>
      <Centralizer>
        <Container style={{
          height: "40vh",
          maxHeight: "50vh",
          position: "static",
          width: "clamp(1000px, 80%, 80vw)"
        }}>
          {addressFormElement()}
          {addresses.length > 0 ?
            <Accordion label={t("addresses")} defaultOpen={true}>
              <Container>
                <DataTable
                  data={addresses.reduce((prev: any, cur) => {
                    let r = { ...cur } as any
                    delete r.contact
                    delete r.countryCode
                    delete r.prefill
                    prev.push(r)
                    return prev
                  }, [])}
                  openCallback={(i) => {
                    setOriginalAddressForm(addresses[i])
                    setAddressForm(addresses[i])
                    addressFormRef.current?.scrollIntoView({ behavior: "smooth" })
                  }}
                />
              </Container>
            </Accordion>
            : <></>

          }
          <Button variant="add" style={{ display: changed ? 'none' : 'inline-block' }} onClick={() => {
            setOriginalAddressForm(undefined)
            setAddressForm({
              addressId: "",
              name: "",
              street1: "",
              zipCode: "",
              city: "",
              countryCode: staticData.warehouse.countryCode,
            })
          }}>{t("newAddress")}</Button>
        </Container>
      </Centralizer>

      <ConfirmDialog options={dialog} />
      <ChangeDialog show={changed}
        undo={async () => {
          setAddressForm(originalAddressForm as AddressType)
        }}
      />
    </Form>
    {addressToDelete ?
      <WriteToDeleteDialog
        title={t("dialogs.sureDeleteAddress")}
        message={t("dialogs.repeatTextToDelete") + addressToDelete}
        textToWrite={addressToDelete}
        onCancel={() => setAddressToDelete("")}
        onOk={() => deleteAddress()}
      />
      : <></>
    }
  </div>)
}

export default AddressRegister